import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  Image,
  ScrollView,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import Skeletons from "../skeleton-components/Skeletons";
import NoDataFound from "../NoDataFound";
import RankWalkthrough from "../walk-through-common-components/profileTooltips/RankWalkthrough";

const BeltRanking = ({
  styles,
  showLoader,
  rankingData,
  setActiveTab,
  scrollViewRef,
}) => {
  const deviceSize = useDeviceSize();

  const isSmallOrXS =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  const renderHeader = () => (
    showLoader ? (
      <Skeletons height={32} width={"100%"} />
    ) : (
      <RankWalkthrough
        setActiveTab={setActiveTab}
        scrollViewRef={scrollViewRef}
        content={
          <View
            style={{
              height: 32,
              flexDirection: "row",
              justifyContent: "space-between",
              backgroundColor: AppColors.gray_1F2937,
              borderRadius: 6,
              borderColor: AppColors.gray_374151,
              alignItems: "center",
              paddingHorizontal: 10,
              gap: isSmallOrXS ? 20 : 0,
              marginBottom: 10,
              minWidth: isSmallOrXS ? 800 : "100%",
            }}
          >
            {[
              "Rank",
              "User name",
              "Win/Loss Ratio",
              "Won Belts",
              "Total views",
              "Gifts received",
              "Total winnings",
            ].map((item, index) => (
              <View
                key={index}
                style={{
                  flex: index === 0 ? 0.5 : 1,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text
                  style={[
                    deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                      ? AppFontStyle.BOLD_ANYBODY_14
                      : AppFontStyle.BOLD_ANYBODY_12,
                    { color: AppColors.white },
                  ]}
                >
                  {item}
                </Text>
              </View>
            ))}
          </View>
        }
      />
    )
  );

  return (
    <View style={[styles.beltsRankingView, { minHeight: 150 }]}>
      {isSmallOrXS ? (
        // Mobile: Header inside ScrollView for horizontal scrolling
        <ScrollView
          horizontal={true}
          contentContainerStyle={{ flexGrow: 1 }}
          showsHorizontalScrollIndicator={true}
          showsVerticalScrollIndicator={true}
          style={{ maxHeight: "100%" }}
        >
          <View style={{ gap: 10, width: "100%", minWidth: 800 }}>
            {renderHeader()}
            {/* Data rows */}
          {rankingData.length === 0 ? (
            <View
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
                minWidth: isSmallOrXS ? 800 : "100%",
              }}
            >
              <NoDataFound />
            </View>
          ) : (
            rankingData.map((item, index) =>
              showLoader ? (
                <Skeletons key={index} height={50} width={"100%"} />
              ) : (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  padding: 6,
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignSelf: "stretch",
                  backgroundColor: AppColors.redOpacity_D1242430,
                  borderRadius: 6,
                  minWidth: isSmallOrXS ? 800 : "100%",
                }}
              >
                <View
                  style={{
                    flex: 0.5,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <LinearGradient
                    colors={[AppColors.buttonRed_DC2626, AppColors.buttonRedDark_991B1B]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{
                      width: 46,
                      height: 40,
                      paddingVertical: 10,
                      paddingHorizontal: 24,
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 10,
                      borderRadius: 6,
                    }}
                  >
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_14,
                        { color: AppColors.white },
                      ]}
                    >
                      {item.rank}
                    </Text>
                  </LinearGradient>
                </View>

                <View
                  style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {item.userId}
                  </Text>
                </View>

                <View
                  style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {item.winGames}/{item.lossGames}
                  </Text>
                </View>

                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Image
                    source={AppImages.icNewMedal}
                    style={{
                      height: 14,
                      width: 10,
                      resizeMode: "contain",
                      marginRight: 5,
                    }}
                  />
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {item.beltsWon}
                  </Text>
                </View>

                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Image
                    source={AppImages.icEye}
                    style={{
                      height: 15,
                      width: 23,
                      resizeMode: "contain",
                      marginRight: 5,
                    }}
                  />
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {item.totalViews}
                  </Text>
                </View>

                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Image
                    source={AppImages.icGift}
                    style={{
                      height: 20,
                      width: 20,
                      resizeMode: "contain",
                      marginRight: 5,
                    }}
                  />
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {item.giftsReceived}
                  </Text>
                </View>

                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {item.totalWinning}
                  </Text>
                </View>
              </View>
            )
          )
        )}
          </View>
        </ScrollView>
      ) : (
        // Desktop: Header outside ScrollView for sticky behavior
        <>
          {renderHeader()}
          <ScrollView
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={true}
            style={{ maxHeight: 310 }}
          >
            <View style={{ gap: 10, width: "100%" }}>
              {rankingData.length === 0 ? (
                <View
                  style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                  }}
                >
                  <NoDataFound />
                </View>
              ) : (
                rankingData.map((item, index) =>
                  showLoader ? (
                    <Skeletons key={index} height={50} width={"100%"} />
                  ) : (
                    <View
                      key={index}
                      style={{
                        flexDirection: "row",
                        padding: 6,
                        justifyContent: "space-between",
                        alignItems: "center",
                        alignSelf: "stretch",
                        backgroundColor: AppColors.redOpacity_D1242430,
                        borderRadius: 6,
                      }}
                    >
                      <View
                        style={{
                          flex: 0.5,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <LinearGradient
                          colors={[AppColors.buttonRed_DC2626, AppColors.buttonRedDark_991B1B]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                          style={{
                            width: 46,
                            height: 40,
                            paddingVertical: 10,
                            paddingHorizontal: 24,
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 10,
                            borderRadius: 6,
                          }}
                        >
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { color: AppColors.white },
                            ]}
                          >
                            {item.rank}
                          </Text>
                        </LinearGradient>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: "white" },
                          ]}
                        >
                          {item.userId}
                        </Text>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: "white" },
                          ]}
                        >
                          {item.winGames}/{item.lossGames}
                        </Text>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Image
                          source={AppImages.icNewMedal}
                          style={{
                            height: 14,
                            width: 10,
                            resizeMode: "contain",
                            marginRight: 5,
                          }}
                        />
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: "white" },
                          ]}
                        >
                          {item.beltsWon}
                        </Text>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Image
                          source={AppImages.icEye}
                          style={{
                            height: 15,
                            width: 23,
                            resizeMode: "contain",
                            marginRight: 5,
                          }}
                        />
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: "white" },
                          ]}
                        >
                          {item.totalViews}
                        </Text>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Image
                          source={AppImages.icGift}
                          style={{
                            height: 20,
                            width: 20,
                            resizeMode: "contain",
                            marginRight: 5,
                          }}
                        />
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: "white" },
                          ]}
                        >
                          {item.giftsReceived}
                        </Text>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: "white" },
                          ]}
                        >
                          {item.totalWinning}
                        </Text>
                      </View>
                    </View>
                  )
                )
              )}
            </View>
          </ScrollView>
        </>
      )}
    </View>
  );
};

export default BeltRanking;
